import { errorCode, retryAfterSeconds } from '@infrastructure/admin-security-api';
import { adminMailErrorDetail } from '@infrastructure/admin-mail-api';
export const normalizeBody = (body) => body.replace(/\r\n?/g, '\n');
export const lineCount = (body) => normalizeBody(body).split('\n').length;
// The rules of the server's AdminMailText (QPortal.Backend.Application/Features/AdminMail/AdminMailText.cs), in the same
// order, so the first issue here is the code the server refuses with. tests/emails/admin-mail-text-cases.json holds the
// cases both sides are tested against.
// Control, format (zero-width, bidi), private-use, unassigned, lone surrogate, line and paragraph separator characters.
// LF is allowed in the body only.
const invalidCharacter = /[\p{Cc}\p{Cf}\p{Co}\p{Cn}\p{Cs}\p{Zl}\p{Zp}]/u;
// `<` followed by an ASCII letter, `/`, `!` or `?`, and template braces.
const tagLike = /<[A-Za-z/!?]|\{\{|\}\}/;
const address = /(?<![\p{L}\p{N}._%+-])[\p{L}\p{N}._%+-]+@[\p{L}\p{N}-]+(?:\.[\p{L}\p{N}-]+)+/gu;
// www.-prefixed and bare host names (label.tld or label.xn--tld, optional path) count as links: mail clients turn them
// into links. Only a letter, digit or @ directly before a host makes it part of a longer word or an address; any
// punctuation before it (".", "-", "+", "_", a no-break space) still leaves a link. A "-" after the top-level label ends
// the host ("evil.com-/login"); the punycode alternative comes first, so "xn--p1ai" is one top-level label.
const bareHost = /(?<![\p{L}\p{N}@])((?:[\p{L}\p{N}](?:[\p{L}\p{N}-]*[\p{L}\p{N}])?\.)+(?:xn--[a-z0-9-]{2,59}|\p{L}{2,63}))(?![\p{L}\p{N}])((?:[/?#]\S*)?)/giu;
// A bare IPv4 literal (with an optional port or path): never a portal host. Dots before it do not hide it
// (".1.2.3.4/login").
const bareIp = /(?<![\p{L}\p{N}@])\d{1,3}(?:\.\d{1,3}){3}(?![\p{L}\p{N}.-])(?:[:/?#]\S*)?/gu;
// Ideographic, full-width and half-width full stops act as dots in host names (IDNA): detection runs on a copy with
// them as "."; it has the same length, so every index and every reported token come from the text as written.
const dotVariants = /[\u3002\uFF0E\uFF61]/g;
// .NET char.IsWhiteSpace, which ends a token on the server.
const isWhite = (c) => /\s/u.test(c) || c === '\u0085';
// Sentence punctuation after a link is not part of it; a closing bracket is, when the token also opened it.
const trimToken = (token) => {
    let value = token;
    while (/[.,;:!?)\]}'"»”’>]$/u.test(value)) {
        const last = value[value.length - 1];
        const open = last === ')' ? '(' : last === ']' ? '[' : last === '}' ? '{' : '';
        if (open && value.split(open).length > value.split(last).length - 1)
            break;
        value = value.slice(0, -1);
    }
    return value;
};
// Every "scheme:" directly followed by a non-space and every "://", whatever character comes before it. The scheme is the
// run of scheme characters before the colon, from its first letter; a scheme of one letter without "//" ("A:ja") is no
// link, so ordinary times ("10:30") are no links either, but an ISO time ("T10:30Z") is. Yields [start, end] of a token.
function* schemeTokens(text) {
    for (let i = 0; i < text.length - 1; i++) {
        if (text[i] !== ':' || isWhite(text[i + 1]))
            continue;
        let run = i;
        while (run > 0 && /[A-Za-z0-9+.-]/.test(text[run - 1]))
            run--;
        while (run < i && !/[A-Za-z]/.test(text[run]))
            run++;
        const slashes = i + 2 < text.length && text[i + 1] === '/' && text[i + 2] === '/';
        if (!slashes && i - run < 2)
            continue;
        let end = run;
        while (end < text.length && !isWhite(text[end]))
            end++;
        yield [run, end];
    }
}
const asciiHost = (host) => {
    try {
        return new URL(`https://${host}`).hostname.toLowerCase();
    }
    catch {
        return null;
    }
};
const ipHost = (host) => /^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.startsWith('[');
// https only; no backslash, no user info, no port (also not :443), a DNS host with a dot (no IP literal, no intranet
// name) on the allowlist, compared in its ASCII (IDN) form, so a homograph never equals the portal host. A link that does
// not parse (for example U+2024 or U+FE52 in the host) is refused, as on the server.
const httpsLinkAllowed = (token, policy) => {
    if (!/^https:\/\//i.test(token) || token.includes('\\'))
        return false;
    const authority = token.slice('https://'.length).split(/[/?#]/)[0];
    if (!authority || /[@:[\]]/.test(authority))
        return false;
    let url;
    try {
        url = new URL(token);
    }
    catch {
        return false;
    }
    const host = url.hostname.toLowerCase();
    return url.protocol === 'https:' && !url.username && !url.password && !url.port && !ipHost(host) && host.includes('.') && policy.hosts.includes(host);
};
const bareHostAllowed = (host, policy) => {
    const ascii = asciiHost(host);
    return !!ascii && !ipHost(ascii) && policy.hosts.includes(ascii);
};
// Same length, so later patterns cannot match across a token that was already judged.
const blank = (value, from, length) => value.slice(0, from) + ' '.repeat(length) + value.slice(from + length);
const contentIssues = (field, value, policy) => {
    const issues = [];
    const characters = field === 'body' ? value.replace(/\n/g, '') : value;
    if (invalidCharacter.test(characters))
        issues.push({ field, code: 'INVALID_CHARACTERS' });
    if (tagLike.test(value))
        issues.push({ field, code: 'PLAIN_TEXT_ONLY' });
    const work = value.replace(dotVariants, '.');
    const written = (start, length) => value.slice(start, start + length);
    let rest = work;
    // A scheme inside an allowed link is judged on its own (`?next=https://evil`); one inside a refused link is not
    // reported twice (the server stops at the first refusal).
    let refusedUntil = -1;
    for (const [start, end] of schemeTokens(work)) {
        const token = trimToken(work.slice(start, end));
        if (start < refusedUntil)
            continue;
        if (!httpsLinkAllowed(token, policy)) {
            issues.push({ field, code: 'LINK_NOT_ALLOWED', token: written(start, token.length) });
            refusedUntil = start + token.length;
        }
        rest = blank(rest, start, token.length);
    }
    for (const match of rest.matchAll(address)) {
        const token = trimToken(match[0]);
        if (!policy.replyTo || token.toLowerCase() !== policy.replyTo.toLowerCase())
            issues.push({ field, code: 'ADDRESS_NOT_ALLOWED', token: written(match.index ?? 0, token.length) });
        rest = blank(rest, match.index ?? 0, match[0].length);
    }
    for (const match of rest.matchAll(bareIp))
        issues.push({ field, code: 'LINK_NOT_ALLOWED', token: written(match.index ?? 0, trimToken(match[0]).length) });
    for (const match of rest.matchAll(bareHost)) {
        if (!bareHostAllowed(match[1], policy))
            issues.push({ field, code: 'LINK_NOT_ALLOWED', token: written(match.index ?? 0, trimToken(match[0]).length) });
    }
    return issues;
};
export const validateSubject = (subject, policy) => {
    const trimmed = subject.trim();
    const issues = [];
    if (/[\r\n\u2028\u2029\u0085]/.test(subject))
        issues.push({ field: 'subject', code: 'SUBJECT_SINGLE_LINE' });
    if (trimmed.length < policy.subjectMin || trimmed.length > policy.subjectMax)
        issues.push({ field: 'subject', code: 'SUBJECT_LENGTH', count: trimmed.length });
    return [...issues, ...contentIssues('subject', trimmed, policy)];
};
export const validateBody = (body, policy) => {
    const normalized = normalizeBody(body);
    const issues = [];
    if (normalized.trim().length < Math.max(1, policy.bodyMin) || normalized.length > policy.bodyMax)
        issues.push({ field: 'body', code: 'BODY_LENGTH', count: normalized.length });
    const lines = normalized.split('\n').length;
    if (lines > policy.bodyMaxLines)
        issues.push({ field: 'body', code: 'BODY_LINES', count: lines });
    return [...issues, ...contentIssues('body', normalized, policy)];
};
// Subject issues first, then body issues, each in the server's order.
export const validateCustomMail = (input, policy) => [
    ...validateSubject(input.subject, policy),
    ...validateBody(input.body, policy)
];
// ---- Dutch texts -----------------------------------------------------------------------------------------------------
const hostList = (policy) => (policy?.hosts.length ? policy.hosts.join(', ') : '');
export const issueText = (issue, policy) => {
    const token = issue.token ? `“${issue.token}”` : '';
    switch (issue.code) {
        case 'SUBJECT_LENGTH':
            return `Het onderwerp moet ${policy?.subjectMin ?? 3} tot ${policy?.subjectMax ?? 150} tekens zijn${issue.count != null ? ` (nu ${issue.count})` : ''}.`;
        case 'SUBJECT_SINGLE_LINE':
            return 'Het onderwerp moet op één regel staan.';
        case 'BODY_LENGTH':
            return `Het bericht moet ${Math.max(1, policy?.bodyMin ?? 1)} tot ${policy?.bodyMax ?? 4000} tekens zijn${issue.count != null ? ` (nu ${issue.count})` : ''}.`;
        case 'BODY_LINES':
            return `Het bericht mag hoogstens ${policy?.bodyMaxLines ?? 50} regels hebben${issue.count != null ? ` (nu ${issue.count})` : ''}.`;
        case 'INVALID_CHARACTERS':
            return 'Verwijder onzichtbare tekens en stuurtekens, zoals tabs, zero-width- of richtingstekens.';
        case 'PLAIN_TEXT_ONLY':
            return 'Alleen platte tekst: geen HTML-tags (zoals <b>) en geen {{ of }}.';
        case 'LINK_NOT_ALLOWED':
            return `${token ? `${token} telt als link en is` : 'Deze link is'} niet toegestaan. ${hostList(policy) ? `Alleen https-links naar ${hostList(policy)} mogen.` : 'Alleen https-links naar QPortal mogen.'} Geen link bedoeld? Formuleer het anders.`;
        case 'ADDRESS_NOT_ALLOWED':
            return `E-mailadressen zijn niet toegestaan${token ? ` (${token})` : ''}${policy?.replyTo ? `, behalve ${policy.replyTo}` : ''}.`;
        default:
            return `Niet toegestaan: ${issue.code}.`;
    }
};
const validatorCodes = new Set(['SUBJECT_LENGTH', 'SUBJECT_SINGLE_LINE', 'BODY_LENGTH', 'BODY_LINES', 'INVALID_CHARACTERS', 'PLAIN_TEXT_ONLY', 'LINK_NOT_ALLOWED', 'ADDRESS_NOT_ALLOWED']);
// A content refusal of the API that names its field (api-contract §6), to show under that field.
export const serverContentIssue = (error) => {
    const code = errorCode(error);
    const detail = adminMailErrorDetail(error);
    return validatorCodes.has(code) && detail.field ? { field: detail.field, code, token: detail.token ?? undefined } : null;
};
const wait = (error) => {
    const seconds = retryAfterSeconds(error);
    if (!seconds)
        return 'later';
    if (seconds < 90)
        return `over ${Math.max(1, Math.ceil(seconds))} seconden`;
    const minutes = Math.ceil(seconds / 60);
    if (minutes < 90)
        return `over ${minutes} ${minutes === 1 ? 'minuut' : 'minuten'}`;
    return `over ${Math.ceil(minutes / 60)} uur`;
};
const notResendable = {
    TEST: 'Een testmail kan niet opnieuw worden verstuurd.',
    DEFINITION: 'Dit soort mail kan niet opnieuw worden verstuurd. Gebruik de eigen flow, bijvoorbeeld een nieuwe uitnodiging of wachtwoordreset.',
    IN_FLIGHT: 'Deze mail wordt nog verwerkt. Wacht tot de verzending is afgerond.',
    NO_REPLAY: 'De oorspronkelijke tekst (zoals de reden van afwijzing) is niet meer beschikbaar, dus deze mail kan niet opnieuw worden verstuurd.'
};
const resendLimit = {
    MAXIMUM: 'Deze mail is al het maximum aantal keren opnieuw verstuurd.',
    IN_FLIGHT: 'De vorige herverzending van deze mail wordt nog verwerkt. Wacht tot die is afgerond.'
};
// SENDER_NAME_NOT_ALLOWED: the admin's own display name, shown in the mail as "Sent by …", is checked like the text and
// never altered, so the admin changes it in the profile.
const senderNameProblem = {
    LINK_NOT_ALLOWED: 'een link',
    ADDRESS_NOT_ALLOWED: 'een e-mailadres',
    INVALID_CHARACTERS: 'onzichtbare tekens of stuurtekens'
};
const senderNameText = (reason, token) => `Je eigen naam staat als “Sent by …” in de mail, maar bevat ${(reason && senderNameProblem[reason]) || 'tekst die niet is toegestaan'}${token ? ` (“${token}”)` : ''}. ` +
    'Pas je naam aan in je profiel en probeer het opnieuw. Er is niets verstuurd.';
const fixedMessages = {
    ADMIN_MAIL_DISABLED: 'Deze functie is uitgeschakeld voor QPortal. Er is niets verstuurd.',
    MESSAGE_NOT_FOUND: 'Deze mail bestaat niet meer. Vernieuw de Verzendhistorie.',
    RECIPIENT_NOT_ALLOWED: 'In deze omgeving mogen alleen testontvangers mail krijgen. Er is niets verstuurd.',
    REQUEST_KEY_REUSED: 'Deze aanvraag is al gebruikt voor een andere mail of andere inhoud. Bekijk het voorbeeld opnieuw en verstuur dan.',
    REQUEST_INVALID: 'De aanvraag is onvolledig. Vernieuw de pagina en probeer het opnieuw.',
    MAIL_OUTCOME_UNKNOWN: 'Het is niet zeker of de mail is verstuurd. Kies opnieuw Versturen: dezelfde aanvraag wordt niet dubbel verstuurd.',
    MAIL_BUSY: 'QPortal verwerkt nu veel mails tegelijk. Kies opnieuw Versturen: dezelfde aanvraag wordt niet dubbel verstuurd.',
    RECIPIENT_NOT_FOUND: 'De ontvanger is niet (meer) te vinden als actief, bevestigd account, of het adres hoort nu bij een ander account dan bij het originele bericht. Er is niets verstuurd. Stuur eventueel een eigen bericht.',
    RECIPIENT_SUPPRESSED: 'Het adres van deze ontvanger is geblokkeerd na een bounce of spamklacht. Er is niets verstuurd.',
    RECIPIENT_CHANGED: 'De ontvanger is gewijzigd sinds het voorbeeld. Bekijk het voorbeeld opnieuw.',
    TEMPLATE_CHANGED: 'De template is gewijzigd sinds het voorbeeld. Controleer het nieuwe voorbeeld en verstuur opnieuw.',
    CONTENT_CHANGED: 'De mail is gewijzigd sinds het voorbeeld: de tekst, het adres van de ontvanger of de templateversie. Bekijk het nieuwe voorbeeld en verstuur opnieuw.',
    MAIL_UNAVAILABLE: 'Deze mail is uitgeschakeld, gepauzeerd of niet gepubliceerd. Er is niets verstuurd.',
    MAIL_NOT_QUEUED: 'De mail kon niet in de wachtrij worden gezet. Er is niets verstuurd.',
    STRONG_SESSION_REQUIRED: 'Log opnieuw in met je passkey of authenticator-app om deze actie te gebruiken.',
    STEP_UP_REQUIRED: 'De server vraagt een extra bevestiging die de portal voor deze actie niet gebruikt. Er is niets verstuurd.',
    SESSION_INVALID: 'Je sessie is verlopen. Log opnieuw in.',
    FORBIDDEN: 'Je hebt geen rechten voor deze actie.'
};
// Messages for the API's UPPER_SNAKE codes (read with the shared errorCode helper). `sending` marks a send: an
// unanswered send may still have been queued, so the admin is told to check the history instead of retrying blindly.
export const adminMailMessage = (error, policy, sending = false) => {
    const code = errorCode(error);
    const detail = adminMailErrorDetail(error);
    if (code === 'RATE_LIMITED')
        return `Je hebt het maximum aantal mails voor nu bereikt. Probeer het ${wait(error)} opnieuw.`;
    if (code === 'RECIPIENT_LIMIT')
        return `Deze ontvanger heeft vandaag al het maximum aantal berichten gekregen. Probeer het ${wait(error)} opnieuw.`;
    if (code === 'PORTAL_LIMIT')
        return `Het dagelijkse maximum voor beheerdersmails in QPortal is bereikt. Probeer het ${wait(error)} opnieuw.`;
    if (code === 'NOT_RESENDABLE')
        return (detail.reason && notResendable[detail.reason]) || 'Deze mail kan niet opnieuw worden verstuurd.';
    if (code === 'RESEND_LIMIT')
        return (detail.reason && resendLimit[detail.reason]) || resendLimit.MAXIMUM;
    if (code === 'SENDER_NAME_NOT_ALLOWED')
        return senderNameText(detail.reason, detail.token);
    if (validatorCodes.has(code))
        return issueText({ code, token: detail.token }, policy);
    if (fixedMessages[code])
        return fixedMessages[code];
    const status = error?.response?.status;
    if (status === 403)
        return fixedMessages.FORBIDDEN;
    if (status === 401)
        return fixedMessages.SESSION_INVALID;
    if (sending && (!status || status >= 500))
        return 'Het is niet zeker of de mail is verstuurd. Kies opnieuw Versturen (dezelfde aanvraag wordt niet dubbel verstuurd) of controleer de Verzendhistorie.';
    return code !== 'REQUEST_FAILED' ? `Bewerking niet voltooid: ${code}` : 'E-mailbeheer is niet beschikbaar. Controleer de verbinding of probeer opnieuw.';
};
export const adminSendLabel = (send) => {
    const who = send.actorName || 'een beheerder';
    const base = send.kind === 'custom' ? `Eigen bericht van ${who}` : `Opnieuw verstuurd door ${who}`;
    return send.source && send.source !== 'Portal' ? `${base} · ${send.source}` : base;
};
